/**
 * CareerAI（ヤギコンシェルジュ）の＋メニュー: 他のAI機能への切替。
 *
 * CareerAI.vue から切り出した（SFC の行数上限に収めるための分割）。
 * 選んだ機能へ**すぐ遷移せず**チャットで一度確認し、「この画面で続ける」なら
 * モードを固定して1問目を出す。固定したまま数往復したら、全画面のほうが
 * レポート等を見られる旨を一度だけ案内する。
 *
 * useCareerAiSend と同じく、必要な ref と関数だけを受け取る。
 */
import type { Ref } from 'vue'
import { ref, watch } from 'vue'

/* eslint-disable @typescript-eslint/no-explicit-any */

type ServiceMenuItem = {
  key: string
  label: string
  description: string
  route: string
  mode: string
}

export type CareerAiServiceMenuContext = {
  messages: Ref<any[]>
  currentMode: Ref<string>
  lockedMode: Ref<string>
  kickoffMode: Ref<string>
  loading: Ref<boolean>
  validModes: string[]
  // モードに切り替えたときにボットが先に出す1問目（表示用。本文の正本はサーバー）
  modeKickoff: Record<string, string>
  // 確認チップ・通知の文言。正本は utils/ai/careerChatMenu.json
  confirmLabels: Record<string, string>
  // 全画面案内を出す画面か（マイページのボットだけ。全画面側では出さない）
  nudgeEnabled: () => boolean
  navigate: (route: string) => Promise<unknown>
  // 固定モードで進めた会話を全画面へ運ぶ（機能ページ側が読んで続きから始める）
  stashConversation: (mode: string, route: string, messages: any[]) => void
  // 遷移に失敗したとき預けた会話を捨てる（残すと別画面が誤って復元する）
  discardConversation: () => void
  onMoved: () => void
  scrollToBottom: () => void
  trackEvent: (eventName: string, params?: any) => void
}

// この往復数だけ固定モードで会話したら全画面を案内する
const FULLSCREEN_NUDGE_AFTER_TURNS = 5

// 会話を受け取れる全画面（utils/ai/careerAiHandover.takeLockedHandover を呼ぶ画面）が
// あるモードだけ預ける。受け取り手の無いモードで預けると、あとで /ai/career が
// 別機能の会話を復元してしまう
const HANDOVER_RECEIVING_MODES = ['interview', 'aptitude', 'selfanalysis']

export function useCareerAiServiceMenu(ctx: CareerAiServiceMenuContext) {
  // 選択済みで移動待ちの機能。**すぐ遷移させず**チャットで一度確認する
  const pendingService = ref<any>(null)
  // 「この画面で続ける」で固定した機能。全画面案内の遷移先に使う
  const lockedItem = ref<ServiceMenuItem | null>(null)
  // 固定した時点のメッセージ数。以後のユーザー発言数を数える起点
  let lockStartIndex = 0
  let nudgeShown = false
  // 遷移中の二度押しを弾く。並行すると片方の失敗が他方の預けた会話まで捨てる
  let navigating = false

  const pushAssistant = (text: string, extra: Record<string, any> = {}) => {
    ctx.messages.value.push({
      role: 'assistant',
      text,
      jobIds: [],
      suggestMode: null,
      modeSwitchDismissed: false,
      ...extra
    })
    ctx.scrollToBottom()
  }

  const fill = (template: unknown, label: string): string =>
    String(template || '').replace('{label}', label)

  const popServiceConfirm = (): void => {
    const last = ctx.messages.value[ctx.messages.value.length - 1]
    if (last && last.isServiceConfirm) ctx.messages.value.pop()
  }

  // 未送信の1問目を取り消す。吹き出しだけ残すと、答えても文脈が付かない
  const dropPendingKickoff = (): void => {
    if (!ctx.kickoffMode.value) return
    ctx.kickoffMode.value = ''
    const last = ctx.messages.value[ctx.messages.value.length - 1]
    if (last && last.isKickoff) ctx.messages.value.pop()
  }

  // 新セッション・機能の指定解除で呼ぶ。古い選択先や案内済みフラグを持ち込まない
  const resetServiceState = (): void => {
    pendingService.value = null
    lockedItem.value = null
    nudgeShown = false
  }

  const onServiceMenuSelect = (item: ServiceMenuItem | null): void => {
    if (!item) return
    ctx.trackEvent('career_ai_service_menu_click', { target: item.key })
    // 遷移先が無い項目（ヤギッシュの使いかた）はこの画面のまま会話を続ける。
    // モードはサーバー応答でしか変わらず、career / inquiry へ移ったままだと
    // コンシェルジュに戻れないため、選んだモードへ明示的に戻す
    if (!item.route) {
      // コンシェルジュへ戻す＝機能の指定を外す。以後はサーバー主導の
      // モード切替（求人提案・問い合わせ）を再び許可する
      if (ctx.validModes.indexOf(item.key) >= 0) {
        ctx.currentMode.value = item.key
      }
      ctx.lockedMode.value = ''
      lockedItem.value = null
      // 出しっぱなしの確認チップ（前の機能への全画面案内など）は捨てる。
      // 残すと「全画面で開く」が選び直した機能ではなく前の機能へ飛ぶ
      if (pendingService.value) {
        pendingService.value = null
        popServiceConfirm()
      }
      dropPendingKickoff()
      return
    }
    pendingService.value = item
    // この画面で続けられる（モードがある）機能は二択、
    // 相当機能が無いものは全画面へ移るかだけを聞く
    pushAssistant(
      fill(
        item.mode
          ? ctx.confirmLabels.question
          : ctx.confirmLabels.questionFullscreenOnly,
        item.label
      ),
      // 「解決しましたか？」の対象にしない（回答ではなく確認文のため）
      { isServiceConfirm: true }
    )
  }

  // **遷移が成立してから**モーダルを閉じる。先に閉じると、遷移が失敗したときに
  // 「閉じただけで何も起きない」状態になり、失敗が握り潰される
  const confirmServiceMove = async (): Promise<void> => {
    const item = pendingService.value
    // 応答中は移らない（途中の発言と空の吹き出しを引き継いでしまう。ヘッダーの全画面ボタンと同じ扱い）
    if (!item || ctx.loading.value || navigating) return
    navigating = true
    ctx.trackEvent('career_ai_service_menu_confirm', {
      target: item.key,
      from: item.isNudge ? 'nudge' : 'menu'
    })
    // この画面で進めた分は置いていかない。固定してからの会話だけ渡す
    // （挨拶や切替前のやり取りは面接の会話ではない）
    const handsOver =
      !!item.mode &&
      ctx.lockedMode.value === item.mode &&
      HANDOVER_RECEIVING_MODES.indexOf(item.mode) >= 0
    if (handsOver) {
      ctx.stashConversation(
        item.mode,
        item.route,
        ctx.messages.value.slice(lockStartIndex)
      )
    }
    try {
      await ctx.navigate(item.route)
      pendingService.value = null
      ctx.onMoved()
    } catch (error) {
      if (handsOver) ctx.discardConversation()
      // 選択状態は消さない。消すと確認文だけが残って再試行できなくなる
      ctx.trackEvent('career_ai_service_menu_navigate_failed', {
        target: item.key,
        message: (error as { message?: string })?.message || ''
      })
      ctx.messages.value.push({
        role: 'system-notice',
        text: '画面を開けませんでした。お手数ですが、もう一度お試しください。',
        jobIds: [],
        suggestMode: null,
        modeSwitchDismissed: false
      })
      ctx.scrollToBottom()
    } finally {
      navigating = false
    }
  }

  // 全画面へ移らず、マイページのボットのまま該当モードで続ける。
  // モード別プロンプトはサーバー側（prompts/career_ai/system_prompts.py）にある
  const stayInServiceMode = (): void => {
    const item = pendingService.value
    pendingService.value = null
    if (!item || !item.mode) return
    ctx.trackEvent('career_ai_service_menu_stay', { target: item.key })
    popServiceConfirm()
    if (ctx.validModes.indexOf(item.mode) >= 0) {
      ctx.currentMode.value = item.mode
    }
    // 以後この機能に固定する（サーバー主導のモード変更を止める）
    ctx.lockedMode.value = item.mode
    lockedItem.value = item
    lockStartIndex = ctx.messages.value.length
    nudgeShown = false
    // 切り替わったことが分かるように通知を1件出す
    ctx.messages.value.push({
      role: 'system-notice',
      text: fill(ctx.confirmLabels.stayNotice, item.label),
      jobIds: [],
      suggestMode: null,
      modeSwitchDismissed: false
    })
    // 1問目はここで出す。ただし画面に積むだけでは AI に見えないので、
    // 次の送信で kickoff_mode として渡して履歴に入れてもらう
    const kickoff = ctx.modeKickoff[item.mode] || ''
    if (kickoff) {
      ctx.kickoffMode.value = item.mode
      // 未送信のまま取り消すとき、この吹き出しだけを消せるようにする
      pushAssistant(kickoff, { isKickoff: true })
      return
    }
    ctx.scrollToBottom()
  }

  const cancelServiceMove = (): void => {
    const item = pendingService.value
    pendingService.value = null
    if (!item) return
    // 確認文は会話に残さない。残すと回答ボタンの無い問いかけが宙に浮き、
    // 初回サジェスト（messages.length <= 1 が条件）も戻らなくなる
    popServiceConfirm()
    ctx.trackEvent('career_ai_service_menu_cancel', {
      target: item.key,
      from: item.isNudge ? 'nudge' : 'menu'
    })
  }

  // 固定モードで一定回数やり取りしたら、全画面を一度だけ案内する。
  // 応答が終わったタイミング（loading が下りたとき）に出す。
  // 数えるのは**成功した AI 応答**だけ。通信エラー・上限到達の直後に案内しない
  const isCompletedReply = (msg: any): boolean =>
    !!msg &&
    msg.role === 'assistant' &&
    !!msg.text &&
    !msg.isError &&
    !msg.isKickoff &&
    !msg.isServiceConfirm

  // 1往復＝ユーザー発言と、それに続く成功応答。応答が複数バブルに分かれても1回
  const completedRoundsSinceLock = (): number => {
    let rounds = 0
    let awaitingReply = false
    for (const msg of ctx.messages.value.slice(lockStartIndex)) {
      if (msg && msg.role === 'user') {
        awaitingReply = true
      } else if (awaitingReply && isCompletedReply(msg)) {
        rounds += 1
        awaitingReply = false
      }
    }
    return rounds
  }

  watch(
    () => ctx.loading.value,
    (isLoading) => {
      if (isLoading || nudgeShown || pendingService.value) return
      if (!ctx.nudgeEnabled()) return
      const item = lockedItem.value
      if (!item || ctx.lockedMode.value !== item.mode) return
      const last = ctx.messages.value[ctx.messages.value.length - 1]
      if (!isCompletedReply(last)) return
      if (completedRoundsSinceLock() < FULLSCREEN_NUDGE_AFTER_TURNS) return
      nudgeShown = true
      ctx.trackEvent('career_ai_fullscreen_nudge_shown', { target: item.key })
      // 確認チップは pendingService の仕組みをそのまま使う。
      // isNudge のときは「この画面で続ける」ではなく「このまま続ける」（再開始しない）
      pendingService.value = { ...item, isNudge: true }
      pushAssistant(fill(ctx.confirmLabels.fullscreenNudge, item.label), {
        isServiceConfirm: true
      })
    }
  )

  return {
    pendingService,
    dropPendingKickoff,
    resetServiceState,
    onServiceMenuSelect,
    confirmServiceMove,
    stayInServiceMode,
    cancelServiceMove
  }
}
